// libraries.
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

// styles.
import { WrapperModal } from '@domains/polygon-tool/components/styles';

// selectors.
import { getListPolygons } from '@domains/save-polygons/store/slices/selectors/state.selectors';
import {
  getCountry,
  getEmail,
} from '@domains/user/store/slices/user-slice/user-slice';
import {
  getAuditRequested,
} from '@domains/audit-polygons/store/slices/auditPolygons.slice';

// api.
import { useSavePolygonsMutation } from '@domains/save-polygons/store/api-slices/savePolygons.endpoints';
import { useGetInvalidPolygonsQuery } from '@domains/audit-polygons/store/api-slices/auditPolygons.endpoints';

// store.
import { apiAcquisitionsPortal } from '@store/apiSlices/apiSlices';

type Step = 'list' | 'confirm';

type DeletablePolygon = {
  polygon_id: string;
  polygon?: google.maps.LatLngLiteral[];
  polygon_type?: 'standard' | 'blocking';
  median_zone_id?: string | null;
  comment?: string;
  active?: string;
  noMedianZone: boolean;
};

export const DeletePolygonsModal = () => {
  const dispatch = useDispatch();
  const polygons = useSelector(getListPolygons);
  const country = useSelector(getCountry);
  const agent = useSelector(getEmail);
  const auditRequested = useSelector(getAuditRequested);

  const [show, setShow] = useState(false);
  const [step, setStep] = useState<Step>('list');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);

  const [savePolygons, { isLoading, isSuccess, isError }] =
    useSavePolygonsMutation();

  // Reutiliza el cache RTK si la auditoría ya fue ejecutada (sin llamada extra).
  const { data: auditData } = useGetInvalidPolygonsQuery(
    { country },
    { skip: !auditRequested }
  );

  // Fusiona polígonos activos de la zona actual con los polígonos de auditoría
  // que no tienen zona mediana (no llegan vía la query normal por zona).
  const deletablePolygons = useMemo<DeletablePolygon[]>(() => {
    const fromList: DeletablePolygon[] = polygons
      .filter(p => p.active !== '0')
      .map(p => ({ ...p, noMedianZone: false }));

    if (!auditData) return fromList;

    const listIds = new Set(fromList.map(p => p.polygon_id));
    const orphans: DeletablePolygon[] = auditData
      .filter(p => !listIds.has(p.polygon_id))
      .map(p => ({ ...p, noMedianZone: !p.median_zone_id }));

    return [...fromList, ...orphans];
  }, [polygons, auditData]);

  const filteredPolygons = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return deletablePolygons;
    return deletablePolygons.filter(polygon =>
      polygon.polygon_id?.toString().toLowerCase().includes(term)
    );
  }, [deletablePolygons, search]);

  const selectedPolygons = useMemo(
    () => deletablePolygons.filter(p => selectedIds.includes(p.polygon_id)),
    [deletablePolygons, selectedIds]
  );

  const resetState = () => {
    setStep('list');
    setSearch('');
    setSelectedIds([]);
    setConfirmed(false);
  };

  const handleClose = () => {
    if (isLoading) return;
    setShow(false);
    resetState();
  };

  const toggleSelected = (polygonId: string) => {
    setSelectedIds(prev =>
      prev.includes(polygonId)
        ? prev.filter(id => id !== polygonId)
        : [...prev, polygonId]
    );
  };

  const handleDelete = () => {
    const items = selectedPolygons.map(polygon => ({
      polygon_id: polygon.polygon_id + '',
      polygon: polygon.polygon,
      agent,
      median_zone: polygon.median_zone_id?.toString() ?? '',
      active: '0',
      comment: polygon.comment ?? '',
    }));
    savePolygons({ country, items });
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success(
        selectedIds.length > 1
          ? 'Polígonos eliminados correctamente'
          : 'Polígono eliminado correctamente'
      );
      dispatch(apiAcquisitionsPortal.util.invalidateTags(['Polygons']));
      setShow(false);
      resetState();
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      toast.error('Ocurrió un error al eliminar los polígonos');
    }
  }, [isError]);

  return (
    <>
      <Button
        className="btn-habi-white"
        onClick={() => setShow(true)}
        title="Eliminar polígonos por ID"
      >
        Eliminar polígonos
      </Button>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        animation={false}
      >
        <WrapperModal>
          <Modal.Header closeButton={!isLoading}>
            <Modal.Title>
              {step === 'list'
                ? 'Eliminar polígonos'
                : 'Confirmar eliminación'}
            </Modal.Title>
          </Modal.Header>

          {step === 'list' ? (
            <>
              <Modal.Body className="px-1">
                <p className="subtitle">
                  Selecciona por ID los polígonos que deseas eliminar. Incluye
                  cualquier tipo (standard o bloqueo), incluso los que no se
                  pueden borrar desde las tarjetas.
                </p>
                <Form.Control
                  type="text"
                  placeholder="Buscar por ID..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="mb-3"
                  style={{ height: 'auto' }}
                />
                <div
                  style={{
                    maxHeight: '320px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  {filteredPolygons.length === 0 && (
                    <p className="subtitle mb-0">
                      No hay polígonos que coincidan.
                    </p>
                  )}
                  {filteredPolygons.map(polygon => {
                    const isBlocking = polygon.polygon_type === 'blocking';
                    return (
                      <div
                        key={polygon.polygon_id}
                        className="d-flex align-items-start gap-2 p-2"
                        style={{
                          background: '#fff',
                          borderRadius: '6px',
                          borderLeft: isBlocking
                            ? '4px solid #DC3545'
                            : '4px solid transparent',
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          id={`delete-polygon-${polygon.polygon_id}`}
                          checked={selectedIds.includes(polygon.polygon_id)}
                          onChange={() => toggleSelected(polygon.polygon_id)}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <strong>ID: {polygon.polygon_id}</strong>
                            <Badge bg={isBlocking ? 'danger' : 'secondary'}>
                              {isBlocking ? 'Bloqueo' : 'Standard'}
                            </Badge>
                            {polygon.noMedianZone && (
                              <Badge bg="warning" text="dark">
                                Sin zona mediana
                              </Badge>
                            )}
                          </div>
                          <div style={{ fontSize: '13px' }}>
                            {polygon.comment?.trim()
                              ? polygon.comment
                              : 'Sin comentario'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Modal.Body>
              <Modal.Footer className="flex-nowrap border-top-0">
                <Button
                  className="btn-habi-white w-100"
                  onClick={handleClose}
                >
                  Cancelar
                </Button>
                <Button
                  className="btn-habi w-100"
                  disabled={selectedIds.length === 0}
                  onClick={() => setStep('confirm')}
                >
                  Continuar ({selectedIds.length})
                </Button>
              </Modal.Footer>
            </>
          ) : (
            <>
              <Modal.Body className="px-1">
                <p className="subtitle">
                  Vas a eliminar {selectedPolygons.length}{' '}
                  {selectedPolygons.length > 1 ? 'polígonos' : 'polígono'}. Esta
                  acción los marca como inactivos y dejarán de mostrarse.
                </p>
                <div
                  style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    marginBottom: '12px',
                  }}
                >
                  {selectedPolygons.map(polygon => (
                    <div
                      key={polygon.polygon_id}
                      className="d-flex align-items-center gap-2 px-2 py-1"
                      style={{ background: '#fff', borderRadius: '6px' }}
                    >
                      <strong>ID: {polygon.polygon_id}</strong>
                      <Badge
                        bg={
                          polygon.polygon_type === 'blocking'
                            ? 'danger'
                            : 'secondary'
                        }
                      >
                        {polygon.polygon_type === 'blocking'
                          ? 'Bloqueo'
                          : 'Standard'}
                      </Badge>
                      {polygon.noMedianZone && (
                        <Badge bg="warning" text="dark">
                          Sin zona mediana
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
                <Form.Check
                  type="checkbox"
                  id="confirm-delete-polygons"
                  label="Confirmo que deseo eliminar los polígonos seleccionados"
                  checked={confirmed}
                  onChange={e => setConfirmed(e.target.checked)}
                />
              </Modal.Body>
              <Modal.Footer className="flex-nowrap border-top-0">
                <Button
                  className="btn-habi-white w-100"
                  disabled={isLoading}
                  onClick={() => {
                    setStep('list');
                    setConfirmed(false);
                  }}
                >
                  Atrás
                </Button>
                <Button
                  className="btn-habi w-100"
                  disabled={!confirmed || isLoading}
                  onClick={handleDelete}
                >
                  {isLoading ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </Modal.Footer>
            </>
          )}
        </WrapperModal>
      </Modal>
    </>
  );
};
